import {
  TriggerTransactionBookingsClearingReason,
  useTriggerTransactionBookingsClearingReason,
} from "@/queries/transactions/trigger-transaction-bookings-clearing-reason";
import { format } from "date-fns";
import { FileText, Link2, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { InvoiceTransaction, Transaction } from "@/apis/gql/types";
import { SignedAmount } from "@/components/shared/miscellaneous/signed-amount";
import { AsyncButton } from "@/components/ui/button";
import HighlightText from "@/components/ui/highlight-text";
import {
  TypographyCaption,
  TypographyMuted,
  TypographySmall,
} from "@/components/ui/typography";
import { ConfirmBankTransactionBookingsButton } from "@/features/transactions/shared/bank-bookings/confirm-bank-transaction-bookings-button";
import { InvoiceAmountBadge } from "@/features/transactions/shared/invoice-amount-badge";
import { DiscrepancyReason } from "@/lib/constants/discrepancy-reason";
import { InvoiceType } from "@/lib/constants/invoiceStatus";
import { sumJournalEntries } from "@/lib/ledger/journal-entry-utils";

type Props = {
  bankTransaction: Transaction;
  invoices: (InvoiceTransaction & {
    discrepancyRule: DiscrepancyReason;
  })[];
  onConfirmBooking: () => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
};

export const InvoiceSuggestionCell = ({
  bankTransaction,
  invoices,
  onConfirmBooking,
  onUnmatch,
}: Props) => {
  return (
    <div className="flex w-full px-2 my-2">
      <div className="flex flex-col h-auto border bg-gray-50 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 divide-y divide-gray-300 w-full overflow-hidden">
        {invoices.map((invoice) => (
          <InvoiceMatch
            key={invoice.invoiceId}
            invoice={invoice}
            discrepancyRule={invoice.discrepancyRule}
            bankTransaction={bankTransaction}
            onConfirmBooking={onConfirmBooking}
            onUnmatch={onUnmatch}
          />
        ))}
      </div>
    </div>
  );
};

const TriggerTransactionBookingsClearingReasonMap = {
  [DiscrepancyReason.DISCOUNT]:
    TriggerTransactionBookingsClearingReason.DISCOUNT,
  [DiscrepancyReason.FXGAIN]: TriggerTransactionBookingsClearingReason.FXGAIN,
  [DiscrepancyReason.FXLOSS]: TriggerTransactionBookingsClearingReason.FXLOSS,
  [DiscrepancyReason.EXACT_MATCH]: undefined,
};

const InvoiceMatch = ({
  invoice,
  bankTransaction,
  onConfirmBooking,
  onUnmatch,
  discrepancyRule,
}: {
  invoice: InvoiceTransaction;
  bankTransaction: Transaction;
  onConfirmBooking: () => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  discrepancyRule: DiscrepancyReason;
}) => {
  const { t } = useTranslation();
  const [triggerTransactionBookingsClearingReason] =
    useTriggerTransactionBookingsClearingReason();

  const handleConfirmTransactionBooking = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    await triggerTransactionBookingsClearingReason({
      bankTransactionId: bankTransaction.bankTxnId,
      reason: TriggerTransactionBookingsClearingReasonMap[discrepancyRule],
    });

    await onConfirmBooking();
  };

  const handleUnmatch = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    await onUnmatch([invoice.invoiceId]);
  };

  const { debit: totalAmount } = sumJournalEntries(invoice.journalEntries);

  const invoiceTotalAmount = useMemo(() => {
    if (invoice.invoiceType === InvoiceType.RECEIVABLE) {
      return totalAmount.toNumber();
    }
    return totalAmount.toNumber() * -1;
  }, [totalAmount, invoice.invoiceType]);

  const isNegative = invoiceTotalAmount < 0;

  const currency = "EUR"; // we default to EUR for the currency

  return (
    <div className="flex flex-col px-2 py-1.5 justify-center relative overflow-hidden min-w-0 h-auto w-full bg-white shadow-md">
      <div className="flex flex-row gap-2 w-full items-center">
        <div className="h-8 w-8 flex-shrink-0 rounded-lg bg-secondary/50 flex items-center justify-center">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden justify-center min-h-[56px]">
          {invoice.invoiceDate && (
            <div className="flex flex-row items-center gap-1 min-w-0 overflow-hidden">
              <TypographyCaption className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {format(invoice.invoiceDate, "dd.MM.yyyy")}
              </TypographyCaption>
            </div>
          )}
          <TypographySmall className="text-[13px] truncate">
            <HighlightText
              text={invoice.supplierName ?? t("unknown")}
              mark={bankTransaction.counterpartyName ?? ""}
            />
          </TypographySmall>
          <TypographyMuted className="text-[13px] text-muted-foreground truncate">
            <HighlightText
              text={invoice.invoiceNo ?? ""}
              mark={bankTransaction.purpose ?? ""}
              tokenize
            />
          </TypographyMuted>
        </div>
        <div className="flex flex-col items-end justify-center flex-shrink-0 gap-1">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-col items-end">
              <SignedAmount
                amount={Math.abs(invoiceTotalAmount)}
                currency={currency}
                side={isNegative ? "debit" : "credit"}
              />
              <InvoiceAmountBadge
                transaction={bankTransaction}
                invoiceTotalAmount={Math.abs(invoiceTotalAmount)}
                currency={currency} // we default to EUR for the currency
                originalCurrency={invoice.currency}
                originalAmount={invoice.totalAmount}
              />
            </div>
            <AsyncButton
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleUnmatch}
            >
              <X className="w-4 h-4" />
            </AsyncButton>
            <ConfirmationButton
              onConfirmBooking={handleConfirmTransactionBooking}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmationButton = ({
  onConfirmBooking,
}: {
  onConfirmBooking: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void>;
}) => {
  const { t } = useTranslation();

  return (
    <ConfirmBankTransactionBookingsButton
      disabled={false}
      onClick={onConfirmBooking}
      variant="outline"
      className="h-8 gap-1"
      label={t("transactions.accounting.panel.booking.status.approve-short")}
      icon={<Link2 className="w-4 h-4" />}
    />
  );
};
